import { lazy, Suspense } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
} from 'react-router-dom';
import { ProtectedRoute, PublicOnlyRoute } from '@/modules/auth/ProtectedRoute';
import { LoadingSkeleton } from '@/components/ui';

// ─── Lazy-loaded pages ────────────────────────────────────────────────────────
// Auth
const LoginPage        = lazy(() => import('@/modules/auth/pages/LoginPage'));
const SignupPage        = lazy(() => import('@/modules/auth/pages/SignupPage'));
const ForgotPasswordPage = lazy(() => import('@/modules/auth/pages/ForgotPasswordPage'));
const VerifyEmailPage  = lazy(() => import('@/modules/auth/pages/VerifyEmailPage'));

// Role-specific layouts
const SuperAdminLayout  = lazy(() => import('@/modules/superAdmin/SuperAdminLayout'));
const CampusAdminLayout = lazy(() => import('@/modules/campusAdmin/CampusAdminLayout'));
const InstructorLayout  = lazy(() => import('@/modules/instructor/InstructorLayout'));
const StudentLayout     = lazy(() => import('@/modules/student/StudentLayout'));

// Super Admin pages
const SuperAdminDashboard  = lazy(() => import('@/modules/superAdmin/pages/SuperAdminDashboard'));
const InstitutionsPage     = lazy(() => import('@/modules/superAdmin/pages/InstitutionsPage'));
const SAUsersPage          = lazy(() => import('@/modules/superAdmin/pages/UsersPage'));
const AuditLogsPage        = lazy(() => import('@/modules/superAdmin/pages/AuditLogsPage'));
const BillingPage          = lazy(() => import('@/modules/superAdmin/pages/BillingPage'));
const FeatureFlagsPage     = lazy(() => import('@/modules/superAdmin/pages/FeatureFlagsPage'));
const SASettingsPage       = lazy(() => import('@/modules/superAdmin/pages/SettingsPage'));
const LibraryAdminPage     = lazy(() => import('@/modules/superAdmin/pages/LibraryAdminPage'));

// Campus Admin pages
const CampusAdminDashboard = lazy(() => import('@/modules/campusAdmin/pages/CampusAdminDashboard'));
const InstructorsPage      = lazy(() => import('@/modules/campusAdmin/pages/InstructorsPage'));
const StudentsPage         = lazy(() => import('@/modules/campusAdmin/pages/StudentsPage'));
const StudentRecordPage    = lazy(() => import('@/modules/campusAdmin/pages/StudentRecordPage'));
const ProgramsPage         = lazy(() => import('@/modules/campusAdmin/pages/ProgramsPage'));
const IntegrationSettingsPage = lazy(() => import('@/modules/campusAdmin/pages/IntegrationSettingsPage'));
const AccreditationReportPage = lazy(() => import('@/modules/campusAdmin/pages/AccreditationReportPage'));
const CampusAnalyticsPage     = lazy(() => import('@/modules/campusAdmin/pages/CampusAnalyticsPage'));

// Instructor pages
const InstructorDashboard  = lazy(() => import('@/modules/instructor/pages/InstructorDashboard'));
const ClassesPage          = lazy(() => import('@/modules/instructor/pages/ClassesPage'));
const ClassDetailPage      = lazy(() => import('@/modules/instructor/pages/ClassDetailPage'));
const LessonCreatorPage    = lazy(() => import('@/modules/instructor/pages/LessonCreatorPage'));
const ImportLessonPage     = lazy(() => import('@/modules/instructor/pages/ImportLessonPage'));
const TaskSheetBuilderPage = lazy(() => import('@/modules/instructor/pages/TaskSheetBuilderPage'));
const QuizGeneratorPage    = lazy(() => import('@/modules/instructor/pages/QuizGeneratorPage'));
const AssignmentManagerPage = lazy(() => import('@/modules/instructor/pages/AssignmentManagerPage'));
const InspectionCreatorPage = lazy(() => import('@/modules/instructor/pages/InspectionCreatorPage'));
const GradebookPage        = lazy(() => import('@/modules/instructor/pages/GradebookPage'));
const GradeSubmissionPage  = lazy(() => import('@/modules/instructor/pages/GradeSubmissionPage'));
const QuizAnalyticsPage    = lazy(() => import('@/modules/instructor/pages/QuizAnalyticsPage'));
const VaultPage            = lazy(() => import('@/modules/instructor/pages/VaultPage'));
const ElevatEdLibraryPage  = lazy(() => import('@/modules/instructor/pages/ElevatEdLibraryPage'));
const InstructorAnalyticsPage = lazy(() => import('@/modules/instructor/pages/InstructorAnalyticsPage'));

// Student pages
const StudentDashboard     = lazy(() => import('@/modules/student/pages/StudentDashboard'));
const StudentLessonsPage   = lazy(() => import('@/modules/student/pages/StudentLessonsPage'));
const StudentTasksPage     = lazy(() => import('@/modules/student/pages/StudentTasksPage'));
const StudentQuizzesPage   = lazy(() => import('@/modules/student/pages/StudentQuizzesPage'));
const LessonPlayerPage     = lazy(() => import('@/modules/student/pages/LessonPlayerPage'));
const JoinClassPage        = lazy(() => import('@/modules/student/pages/JoinClassPage'));
const TaskSheetPage        = lazy(() => import('@/modules/student/pages/TaskSheetPage'));
const QuizPage             = lazy(() => import('@/modules/student/pages/QuizPage'));
const InspectionPage       = lazy(() => import('@/modules/student/pages/InspectionPage'));
const StudentPortfolioPage = lazy(() => import('@/modules/student/pages/StudentPortfolioPage'));

// Shared / utility pages
const DashboardRedirect    = lazy(() => import('./DashboardRedirect'));
const UnauthorizedPage     = lazy(() => import('./UnauthorizedPage'));
const NotFoundPage         = lazy(() => import('./NotFoundPage'));

// ─── Suspense wrapper ─────────────────────────────────────────────────────────

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-screen bg-[var(--color-bg-primary)]">
      <div className="w-full max-w-xl p-8 space-y-4">
        <LoadingSkeleton className="h-8 w-1/2" />
        <LoadingSkeleton className="h-4 w-full" />
        <LoadingSkeleton className="h-4 w-3/4" />
        <div className="grid grid-cols-3 gap-4 pt-4">
          <LoadingSkeleton className="h-24" />
          <LoadingSkeleton className="h-24" />
          <LoadingSkeleton className="h-24" />
        </div>
      </div>
    </div>
  );
}

function SuspenseLayout() {
  return (
    <Suspense fallback={<PageLoader />}>
      <Outlet />
    </Suspense>
  );
}

// ─── Router definition ────────────────────────────────────────────────────────

const router = createBrowserRouter([
  {
    element: <SuspenseLayout />,
    children: [
      // ── Public (auth) routes ──────────────────────────────────────────
      {
        path: '/login',
        element: <PublicOnlyRoute><LoginPage /></PublicOnlyRoute>,
      },
      {
        path: '/signup',
        element: <PublicOnlyRoute><SignupPage /></PublicOnlyRoute>,
      },
      {
        path: '/forgot-password',
        element: <PublicOnlyRoute><ForgotPasswordPage /></PublicOnlyRoute>,
      },
      {
        path: '/verify-email',
        element: <VerifyEmailPage />,
      },

      // ── Root redirect ─────────────────────────────────────────────────
      {
        path: '/',
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: '/dashboard',
        element: <ProtectedRoute><DashboardRedirect /></ProtectedRoute>,
      },

      // ── Super Admin ───────────────────────────────────────────────────
      {
        path: '/admin',
        element: (
          <ProtectedRoute requiredRoles={['superAdmin']}>
            <SuperAdminLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true,               element: <SuperAdminDashboard /> },
          { path: 'institutions',      element: <InstitutionsPage /> },
          { path: 'users',             element: <SAUsersPage /> },
          { path: 'audit-logs',     element: <AuditLogsPage /> },
          { path: 'billing',        element: <BillingPage /> },
          { path: 'feature-flags',  element: <FeatureFlagsPage /> },
          { path: 'settings',       element: <SASettingsPage /> },
          { path: 'library',        element: <LibraryAdminPage /> },
        ],
      },

      // ── Campus Admin ──────────────────────────────────────────────────
      {
        path: '/campus',
        element: (
          <ProtectedRoute requiredRoles={['campusAdmin', 'superAdmin']}>
            <CampusAdminLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true,                      element: <CampusAdminDashboard /> },
          { path: 'instructors',              element: <InstructorsPage /> },
          { path: 'students',                 element: <StudentsPage /> },
          { path: 'students/:studentId',      element: <StudentRecordPage /> },
          { path: 'programs',                 element: <ProgramsPage /> },
          { path: 'integrations',             element: <IntegrationSettingsPage /> },
          { path: 'accreditation',            element: <AccreditationReportPage /> },
          { path: 'analytics',               element: <CampusAnalyticsPage /> },
        ],
      },

      // ── Instructor ────────────────────────────────────────────────────
      {
        path: '/instructor',
        element: (
          <ProtectedRoute requiredRoles={['instructor', 'campusAdmin', 'superAdmin']}>
            <InstructorLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true,                                   element: <InstructorDashboard /> },
          { path: 'classes',                               element: <ClassesPage /> },
          { path: 'classes/:classId',                      element: <ClassDetailPage /> },
          { path: 'classes/:classId/gradebook',            element: <GradebookPage /> },
          { path: 'classes/:classId/grade/:submissionId',  element: <GradeSubmissionPage /> },
          { path: 'create/lesson',                         element: <LessonCreatorPage /> },
          { path: 'create/lesson/:lessonId',               element: <LessonCreatorPage /> },
          { path: 'lessons/new',                           element: <LessonCreatorPage /> },
          { path: 'import/lesson',                         element: <ImportLessonPage /> },
          { path: 'create/task-sheet',                     element: <TaskSheetBuilderPage /> },
          { path: 'create/task-sheet/:taskId',             element: <TaskSheetBuilderPage /> },
          { path: 'task-sheets/new',                       element: <TaskSheetBuilderPage /> },
          { path: 'create/quiz',                           element: <QuizGeneratorPage /> },
          { path: 'create/quiz/:quizId',                   element: <QuizGeneratorPage /> },
          { path: 'quizzes/new',                           element: <QuizGeneratorPage /> },
          { path: 'create/inspection',                        element: <InspectionCreatorPage /> },
          { path: 'inspections/new',                          element: <InspectionCreatorPage /> },
          { path: 'assignments',                           element: <AssignmentManagerPage /> },
          { path: 'gradebook',                             element: <GradebookPage /> },
          { path: 'gradebook/:submissionId',               element: <GradeSubmissionPage /> },
          { path: 'quiz-analytics/:quizId',                 element: <QuizAnalyticsPage /> },
          { path: 'vault',                                 element: <VaultPage /> },
          { path: 'library',                               element: <ElevatEdLibraryPage /> },
          { path: 'analytics',                             element: <InstructorAnalyticsPage /> },
          { path: 'analytics/:classId',                    element: <InstructorAnalyticsPage /> },
        ],
      },

      // ── Student ───────────────────────────────────────────────────────
      {
        path: '/student',
        element: (
          <ProtectedRoute requiredRoles={['student']}>
            <StudentLayout />
          </ProtectedRoute>
        ),
        children: [
          { index: true,                                element: <StudentDashboard /> },
          { path: 'lessons',                            element: <StudentLessonsPage /> },
          { path: 'tasks',                              element: <StudentTasksPage /> },
          { path: 'quizzes',                            element: <StudentQuizzesPage /> },
          { path: 'lessons/:lessonId',                       element: <LessonPlayerPage /> },
          { path: 'tasks/:taskSheetId',                        element: <TaskSheetPage /> },
          { path: 'quizzes/:quizId',                           element: <QuizPage /> },
          { path: 'inspection/:inspectionId',                  element: <InspectionPage /> },
          { path: 'portfolio',                          element: <StudentPortfolioPage /> },
          { path: 'join',                               element: <JoinClassPage /> },
        ],
      },

      // ── Utility ───────────────────────────────────────────────────────
      { path: '/unauthorized', element: <UnauthorizedPage /> },
      { path: '*',             element: <NotFoundPage /> },
    ],
  },
]);

export function AppRouter() {
  return <RouterProvider router={router} />;
}
